import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { api } from '../lib/auth';
import { Button, Input, Select, Pill, PageHeader, Stat, StatRow } from '../lib/ui';

// ── Shared helpers ────────────────────────────────────────────────────────────
function formatGBP(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return '0.00';
  return x.toFixed(2);
}
function clampPrice(v) {
  const n = Number.parseFloat(String(v ?? '').replace(/[^\d.]/g, ''));
  if (Number.isNaN(n)) return 0;
  return Math.max(0, n);
}
function monthLabel(key) {
  const [y, m] = key.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString('en-GB', { month: 'short', year: '2-digit' });
}


const th = { textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: 'var(--muted)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '.04em' };
const td = { padding: '10px 12px', verticalAlign: 'middle', fontSize: 13, color: 'var(--text)', maxWidth: 800, overflow: 'hidden', textOverflow: 'ellipsis' };
const labelStyle = { fontSize: '11px', fontWeight: 600, color: 'var(--muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.03em' };

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, prefix = '£', tone }) {
  const colors = {
    income:  { accent: 'var(--ok-solid)', text: 'var(--ok-fg)' },
    expense: { accent: 'var(--bad-fg)',   text: 'var(--bad-fg)' },
    profit:  { accent: 'var(--info-fg)',  text: 'var(--info-fg)' },
    neutral: { accent: 'var(--border-strong)', text: 'var(--text)' },
  };
  const c = colors[tone] || colors.neutral;
  const number = prefix === '£' ? formatGBP(value) : String(value);
  return <Stat label={label} value={`${prefix}${number}`} accent={c.accent} valueColor={c.text} />;
}

// ── Month-by-month P&L chart (pure CSS bars) ──────────────────────────────────
function MonthlyChart({ monthly }) {
  const months = useMemo(() => {
    const map = {};
    monthly.forEach(r => {
      const key = `${r._id.year}-${String(r._id.month).padStart(2, '0')}`;
      if (!map[key]) map[key] = { income: 0, expense: 0 };
      map[key][r._id.type] = r.total;
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b)).slice(-12);
  }, [monthly]);

  if (!months.length) return null;

  const max = Math.max(...months.flatMap(([, v]) => [v.income, v.expense]), 1);

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 120, padding: '0 4px' }}>
        {months.map(([key, v]) => (
          <div key={key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <div style={{ width: '100%', display: 'flex', gap: 2, alignItems: 'flex-end', height: 100 }}>
              <div style={{
                flex: 1, background: '#c3e6cb', borderRadius: '4px 4px 0 0',
                height: `${(v.income / max) * 100}%`, minHeight: v.income ? 2 : 0,
                transition: 'height 0.4s',
              }} title={`Income: £${formatGBP(v.income)}`} />
              <div style={{
                flex: 1, background: '#f5c6cb', borderRadius: '4px 4px 0 0',
                height: `${(v.expense / max) * 100}%`, minHeight: v.expense ? 2 : 0,
                transition: 'height 0.4s',
              }} title={`Expense: £${formatGBP(v.expense)}`} />
            </div>
            <div style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center', whiteSpace: 'nowrap' }}>{monthLabel(key)}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11, color: 'var(--muted)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, background: '#c3e6cb', display: 'inline-block', borderRadius: 2 }} /> Income</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, background: '#f5c6cb', display: 'inline-block', borderRadius: 2 }} /> Expenses</span>
      </div>
    </div>
  );
}

// ── Add Transaction modal ─────────────────────────────────────────────────────
function AddTransactionPanel({ isAdmin, categories, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({
    type: 'expense', amount: '', description: '', category: '',
    customCategory: '', date: new Date().toISOString().slice(0, 10), notes: '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setErr('');
    if (!form.description.trim()) return setErr('Description is required');
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return setErr('Enter a valid amount');
    const cat = form.category === '__custom' ? form.customCategory : form.category;
    if (!cat) return setErr('Category is required');

    setSaving(true);
    try {
      await api.post('/accounting/transactions', {
        type: form.type, amount: amt, description: form.description.trim(),
        category: cat, date: form.date, notes: form.notes, source: 'manual',
      });
      onCreated?.();
      setOpen(false);
      setForm({ type: 'expense', amount: '', description: '', category: '', customCategory: '', date: new Date().toISOString().slice(0, 10), notes: '' });
    } catch (e) { setErr(e?.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: '10px 14px', background: 'var(--bg)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <b>Log transaction</b>
        <Button onClick={() => setOpen(o => !o)} disabled={!isAdmin}>{open ? 'Close' : '+ New transaction'}</Button>
      </div>
      {open && (
        <div style={{ padding: 14, background: 'var(--panel)' }}>
          {err && <div style={{ marginBottom: 10, padding: 10, borderRadius: 'var(--r-lg)', background: '#ffecec', color: '#b00' }}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <div style={labelStyle}>Type</div>
              <Select value={form.type} onChange={e => set('type', e.target.value)}>
                <option value="expense">Expense</option>
                <option value="income">Income</option>
              </Select>
            </div>
            <div>
              <div style={labelStyle}>Amount (£)</div>
              <Input value={form.amount} onChange={e => set('amount', e.target.value)} inputMode="decimal" placeholder="0.00" />
            </div>
            <div>
              <div style={labelStyle}>Date</div>
              <Input type="date" value={form.date} onChange={e => set('date', e.target.value)} />
            </div>
            <div>
              <div style={labelStyle}>Category</div>
              <Select value={form.category} onChange={e => set('category', e.target.value)}>
                <option value="">Select…</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                <option value="__custom">+ New category</option>
              </Select>
              {form.category === '__custom' && (
                <Input style={{ marginTop: 6 }} placeholder="Category name" value={form.customCategory} onChange={e => set('customCategory', e.target.value)} />
              )}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <div style={labelStyle}>Description</div>
              <Input value={form.description} onChange={e => set('description', e.target.value)} placeholder="What was this for?" />
            </div>
            <div>
              <div style={labelStyle}>Notes (optional)</div>
              <Input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Extra detail…" />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CSV Import panel ──────────────────────────────────────────────────────────
function ImportPanel({ isAdmin, onImported }) {
  const [open, setOpen] = useState(false);
  const [importType, setImportType] = useState('amazon');
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const fileRef = useRef();

  const run = async () => {
    if (!file) return setErr('Select a file first');
    setErr(''); setResult(null); setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/accounting/import/${importType}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      onImported?.();
    } catch (e) { setErr(e?.response?.data?.error || 'Import failed'); }
    finally { setImporting(false); }
  };

  const hints = {
    amazon: 'Download from amazon.co.uk → Account → Order History → Request Report (CSV)',
    stripe: 'Download from Stripe Dashboard → Payouts → Export CSV',
    generic: 'CSV with columns: date, amount, description, category, type (income/expense), notes',
  };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: '10px 14px', background: 'var(--bg)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <b>Bulk import CSV</b>
        <Button onClick={() => setOpen(o => !o)} disabled={!isAdmin}>{open ? 'Close' : '↑ Import CSV'}</Button>
      </div>
      {open && (
        <div style={{ padding: 14, background: 'var(--panel)' }}>
          {err && <div style={{ marginBottom: 10, padding: 10, borderRadius: 'var(--r-lg)', background: '#ffecec', color: '#b00' }}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 12, marginBottom: 10 }}>
            <div>
              <div style={labelStyle}>Import format</div>
              <Select value={importType} onChange={e => { setImportType(e.target.value); setResult(null); setErr(''); }}>
                <option value="amazon">Amazon Order History</option>
                <option value="stripe">Stripe Payouts</option>
                <option value="generic">Generic CSV</option>
              </Select>
            </div>
            <div>
              <div style={labelStyle}>File</div>
              <input
                ref={fileRef} type="file" accept=".csv"
                onChange={e => { setFile(e.target.files[0]); setResult(null); setErr(''); }}
                style={{ marginTop: 4 }}
              />
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', background: 'var(--bg)', borderRadius: 'var(--r-xl)', padding: '8px 12px', marginBottom: 12 }}>
            💡 {hints[importType]}
          </div>
          {result && (
            <div style={{ marginBottom: 12, padding: 10, borderRadius: 'var(--r-lg)', background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', fontSize: 13 }}>
              ✓ {result.imported} imported · {result.skipped} skipped (duplicates/blanks)
              {result.errors?.length > 0 && <div style={{ color: '#b00', marginTop: 4 }}>{result.errors.slice(0, 3).join(' · ')}</div>}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={run} disabled={importing || !file}>
              {importing ? 'Importing…' : 'Import'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Edit row inline ───────────────────────────────────────────────────────────
function EditableRow({ tx, categories, onSaved, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setForm({
      description: tx.description,
      amount:      formatGBP(tx.amount),
      category:    tx.category,
      date:        tx.date?.slice(0, 10) || '',
      notes:       tx.notes || '',
      type:        tx.type,
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/accounting/transactions/${tx.id}`, {
        ...form, amount: parseFloat(form.amount),
      });
      onSaved?.();
      setEditing(false);
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${tx.description}"?`)) return;
    try { await api.delete(`/accounting/transactions/${tx.id}`); onDeleted?.(); }
    catch { /* ignore */ }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const sourceLabel = { manual: '', stripe: '🔵 Stripe', amazon_csv: '📦 Amazon', csv_import: '📄 CSV' };

  if (editing) {
    return (
      <React.Fragment>
        <tr style={{ background: '#fffdf3', borderBottom: '1px solid var(--border)' }}>
          <td style={td} colSpan={7}>
            <div style={{ display: 'grid', gridTemplateColumns: '100px 100px 1fr 1fr 120px 1fr', gap: 8, alignItems: 'end' }}>
              <div>
                <div style={labelStyle}>Type</div>
                <Select value={form.type} onChange={e => set('type', e.target.value)} style={{ fontSize: 12 }}>
                  <option value="expense">Expense</option>
                  <option value="income">Income</option>
                </Select>
              </div>
              <div>
                <div style={labelStyle}>Amount £</div>
                <Input value={form.amount} onChange={e => set('amount', e.target.value)} style={{ fontSize: 12 }} />
              </div>
              <div>
                <div style={labelStyle}>Description</div>
                <Input value={form.description} onChange={e => set('description', e.target.value)} style={{ fontSize: 12 }} />
              </div>
              <div>
                <div style={labelStyle}>Category</div>
                <Select value={form.category} onChange={e => set('category', e.target.value)} style={{ fontSize: 12 }}>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </Select>
              </div>
              <div>
                <div style={labelStyle}>Date</div>
                <Input type="date" value={form.date} onChange={e => set('date', e.target.value)} style={{ fontSize: 12 }} />
              </div>
              <div>
                <div style={labelStyle}>Notes</div>
                <Input value={form.notes} onChange={e => set('notes', e.target.value)} style={{ fontSize: 12 }} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 10 }}>
              <Button onClick={() => setEditing(false)} style={{ fontSize: 12, padding: '6px 10px' }}>Cancel</Button>
              <Button variant="primary" onClick={save} disabled={saving} style={{ fontSize: 12, padding: '6px 10px' }}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </td>
        </tr>
      </React.Fragment>
    );
  }

  return (
    <tr style={{ background: 'var(--panel)', borderBottom: '1px solid var(--border)' }}>
      <td style={td}>
        <Pill tone={tx.type === 'income' ? 'income' : 'expense'}>
          {tx.type === 'income' ? '↑ IN' : '↓ OUT'}
        </Pill>
      </td>
      <td style={{ ...td, color: 'var(--muted)', fontSize: 12 }}>
        {tx.date ? new Date(tx.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}
      </td>
      <td style={td}>
        <div style={{ fontWeight: 500 }}>{tx.description}</div>
        {tx.notes && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{tx.notes}</div>}
      </td>
      <td style={td}><Pill tone="blue">{tx.category}</Pill></td>
      <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: tx.type === 'income' ? '#1a6e35' : '#b00020' }}>
        {tx.type === 'income' ? '+' : '−'}£{formatGBP(tx.amount)}
      </td>
      <td style={{ ...td, fontSize: 11, color: 'var(--muted)' }}>{sourceLabel[tx.source] || ''}</td>
      <td style={td}>
        <div style={{ display: 'flex', gap: 4 }}>
          <Button onClick={startEdit} style={{ fontSize: 12, padding: '5px 9px' }}>Edit</Button>
          <Button variant="danger" onClick={del} style={{ fontSize: 12, padding: '5px 9px' }}>Del</Button>
        </div>
      </td>
    </tr>
  );
}

// ── Category breakdown tab ────────────────────────────────────────────────────
function CategoryBreakdown({ categories }) {
  if (!categories.length) return <div style={{ color: 'var(--muted)', fontSize: 13, padding: 20 }}>No expense data yet.</div>;
  const total = categories.reduce((s, c) => s + c.total, 0);
  return (
    <div>
      {categories.map(c => (
        <div key={c._id} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontSize: 13 }}>
            <span style={{ fontWeight: 500 }}>{c._id}</span>
            <span style={{ color: '#b00020', fontWeight: 600 }}>£{formatGBP(c.total)} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>({c.count} tx)</span></span>
          </div>
          <div style={{ height: 6, borderRadius: 'var(--r-sm)', background: 'var(--panel-2)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(100, (c.total / total) * 100)}%`, background: '#f5c6cb', borderRadius: 'var(--r-sm)', transition: 'width 0.5s' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Cash flow forecast tab ────────────────────────────────────────────────────
// Pure client-side projection over the existing monthly income/expense
// aggregates — no new backend endpoint or schema. "Running balance" is the
// cumulative income-minus-expense recorded in this system, used as a proxy
// for reserves since there's no separate bank-balance field to draw from.
function CashFlowForecast({ monthly }) {
  const forecast = useMemo(() => {
    if (!monthly || !monthly.length) return null;

    const map = {};
    monthly.forEach(r => {
      const key = `${r._id.year}-${String(r._id.month).padStart(2, '0')}`;
      if (!map[key]) map[key] = { income: 0, expense: 0 };
      map[key][r._id.type] = r.total;
    });

    const keys = Object.keys(map).sort();
    if (!keys.length) return null;

    const now = new Date();
    const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const completeKeys = keys.filter(k => k !== currentKey);
    const trailing = (completeKeys.length ? completeKeys : keys).slice(-3);

    const avg = (field) => trailing.reduce((s, k) => s + (map[k][field] || 0), 0) / trailing.length;
    const avgIncome = avg('income');
    const avgExpense = avg('expense');
    const avgNet = avgIncome - avgExpense;

    const cumulativeNet = keys.reduce((s, k) => s + (map[k].income - map[k].expense), 0);

    let status, statusLabel, statusDetail;
    if (avgNet >= 0) {
      status = 'ok';
      statusLabel = 'Building reserves';
      statusDetail = `Averaging +£${formatGBP(avgNet)}/month over the last ${trailing.length} month${trailing.length === 1 ? '' : 's'}`;
    } else if (cumulativeNet <= 0) {
      status = 'bad';
      statusLabel = 'Already in deficit';
      statusDetail = 'Recorded expenses exceed recorded income to date';
    } else {
      const months = cumulativeNet / Math.abs(avgNet);
      status = months < 3 ? 'bad' : months < 6 ? 'warn' : 'ok';
      statusLabel = `${months.toFixed(1)} month${months >= 1.05 ? 's' : ''} runway`;
      statusDetail = `At the current burn rate of £${formatGBP(Math.abs(avgNet))}/month`;
    }

    const historical = keys.slice(-6).map(k => ({ key: k, label: monthLabel(k), net: map[k].income - map[k].expense, forecast: false }));

    const projected = [1, 2, 3].map(i => {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      return { key, label: monthLabel(key), net: avgNet, forecast: true, cumulative: cumulativeNet + avgNet * i };
    });

    return { avgIncome, avgExpense, avgNet, cumulativeNet, status, statusLabel, statusDetail, historical, projected, trailingCount: trailing.length };
  }, [monthly]);

  if (!forecast) return <div style={{ color: 'var(--muted)', fontSize: 13, padding: 20 }}>Not enough transaction history yet to forecast.</div>;

  const { avgIncome, avgExpense, avgNet, cumulativeNet, status, statusLabel, statusDetail, historical, projected, trailingCount } = forecast;
  const bars = [...historical, ...projected];
  const maxAbs = Math.max(...bars.map(b => Math.abs(b.net)), 1);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Avg Monthly Income" value={avgIncome} tone="income" />
        <StatCard label="Avg Monthly Expense" value={avgExpense} tone="expense" />
        <StatCard label="Avg Monthly Net" value={avgNet} tone={avgNet >= 0 ? 'profit' : 'expense'} />
        <div style={{
          flex: 1, minWidth: 200, padding: '16px 20px', borderRadius: 'var(--r-xl)',
          background: 'var(--panel-2)', border: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Runway</div>
          <div style={{ marginBottom: 4 }}><Pill tone={status}>{statusLabel}</Pill></div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{statusDetail}</div>
        </div>
      </div>

      <div style={{ marginBottom: 4, fontWeight: 600, color: 'var(--text)', fontSize: 13 }}>Monthly net (actual + 3-month projection)</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 120, padding: '0 4px', marginTop: 8 }}>
        {bars.map(b => (
          <div key={b.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <div style={{ width: '100%', height: 100, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <div style={{
                width: '100%',
                height: `${(Math.abs(b.net) / maxAbs) * 100}%`,
                minHeight: b.net ? 2 : 0,
                background: b.net >= 0 ? '#c3e6cb' : '#f5c6cb',
                borderRadius: '4px 4px 0 0',
                opacity: b.forecast ? 0.55 : 1,
                border: b.forecast ? `1px dashed ${b.net >= 0 ? '#1a6e35' : '#b00020'}` : 'none',
                boxSizing: 'border-box',
                transition: 'height 0.4s',
              }} title={`${b.forecast ? 'Projected' : 'Actual'} net: ${b.net >= 0 ? '+' : '−'}£${formatGBP(Math.abs(b.net))}`} />
            </div>
            <div style={{ fontSize: 9, color: b.forecast ? 'var(--muted)' : 'var(--text)', fontStyle: b.forecast ? 'italic' : 'normal', textAlign: 'center', whiteSpace: 'nowrap' }}>{b.label}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 24 }}>
        <div style={{ marginBottom: 8, fontWeight: 600, color: 'var(--text)', fontSize: 13 }}>Projected reserves</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', maxWidth: 520 }}>
          <thead>
            <tr style={{ background: 'var(--bg)' }}>
              <th style={th}>Month</th>
              <th style={{ ...th, textAlign: 'right' }}>Projected net</th>
              <th style={{ ...th, textAlign: 'right' }}>Running balance</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={td}><i>Today</i></td>
              <td style={{ ...td, textAlign: 'right' }}>—</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>£{formatGBP(cumulativeNet)}</td>
            </tr>
            {projected.map(p => (
              <tr key={p.key} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={td}>{p.label}</td>
                <td style={{ ...td, textAlign: 'right', color: p.net >= 0 ? '#1a6e35' : '#b00020' }}>{p.net >= 0 ? '+' : '−'}£{formatGBP(Math.abs(p.net))}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>£{formatGBP(p.cumulative)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 12, maxWidth: 520 }}>
        Forecast assumes the last {trailingCount} recorded month{trailingCount === 1 ? '' : 's'} continue at the same average pace. "Running balance" is cumulative income minus expenses recorded in this system — not a live bank balance — so treat it as a directional signal, not a guarantee.
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN ACCOUNTING PAGE
// ══════════════════════════════════════════════════════════════════════════════
const ADMIN_ROLES = ['admin', 'administrator', 'owner', 'superadmin'];

export default function AccountingPage() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('transactions');

  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(false);
  const [page, setPage]                 = useState(1);
  const LIMIT = 50;

  // Filters
  const [q, setQ]               = useState('');
  const [filterType, setFilterType]     = useState('');
  const [filterCat, setFilterCat]       = useState('');
  const [filterFrom, setFilterFrom]     = useState('');
  const [filterTo, setFilterTo]         = useState('');

  // Summary
  const [summary, setSummary]           = useState(null);
  const [allTimeSummary, setAllTimeSummary] = useState(null);
  const [categories, setCategories]     = useState([]);
  const [banner, setBanner]             = useState('');
  const bannerRef = useRef();

  const flash = (msg) => {
    setBanner(msg);
    if (bannerRef.current) clearTimeout(bannerRef.current);
    bannerRef.current = setTimeout(() => setBanner(''), 2500);
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get('/auth/me');
        const r = (me?.data?.user?.role || '').toLowerCase();
        setIsAdmin(ADMIN_ROLES.includes(r));
      } catch { setIsAdmin(false); }
    })();
  }, []);

  const loadTransactions = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: LIMIT });
      if (q)          params.set('q', q);
      if (filterType) params.set('type', filterType);
      if (filterCat)  params.set('category', filterCat);
      if (filterFrom) params.set('from', filterFrom);
      if (filterTo)   params.set('to', filterTo);

      const r = await api.get(`/accounting/transactions?${params}`);
      setTransactions(r.data.transactions || []);
      setTotal(r.data.total || 0);
      setPage(pg);
    } catch { flash('Failed to load transactions'); }
    finally { setLoading(false); }
  }, [q, filterType, filterCat, filterFrom, filterTo]);

  const loadSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterFrom) params.set('from', filterFrom);
      if (filterTo)   params.set('to', filterTo);
      const r = await api.get(`/accounting/summary?${params}`);
      setSummary(r.data);
    } catch {}
  }, [filterFrom, filterTo]);

  const loadCategories = useCallback(async () => {
    try {
      const r = await api.get('/accounting/categories');
      setCategories(r.data || []);
    } catch {}
  }, []);

  // Always unfiltered (ignores the date-range filters), so the cash flow
  // forecast reflects the full trend even when Overview/Categories are scoped
  // to a narrower date range.
  const loadAllTimeSummary = useCallback(async () => {
    try {
      const r = await api.get('/accounting/summary');
      setAllTimeSummary(r.data);
    } catch {}
  }, []);

  useEffect(() => { loadTransactions(1); loadSummary(); loadCategories(); }, [filterType, filterCat, filterFrom, filterTo]);
  useEffect(() => { loadAllTimeSummary(); }, [loadAllTimeSummary]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => loadTransactions(1), 350);
    return () => clearTimeout(t);
  }, [q]);

  const refresh = () => { loadTransactions(1); loadSummary(); loadCategories(); loadAllTimeSummary(); flash('Refreshed'); };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="dashboard-container accounting-page">
      <PageHeader
        title="Finance"
        subtitle="Income, expenses and cash flow"
        actions={<Button onClick={refresh}>↻ Refresh</Button>}
        tabs={[
          { key: 'transactions', label: 'Transactions' },
          { key: 'overview',     label: 'P&L overview' },
          { key: 'categories',   label: 'By category' },
          { key: 'forecast',     label: 'Cash flow' },
          { key: 'import',       label: 'Import' },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {banner && <div style={{ padding: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: 12, borderRadius: 'var(--r-lg)', fontSize: 13, color: '#15803d' }}>{banner}</div>}

      {/* Summary stat cards */}
      {summary && (
        <StatRow>
          <StatCard label="Total Income"   value={summary.totals.income}  tone="income" />
          <StatCard label="Total Expenses" value={summary.totals.expense} tone="expense" />
          <StatCard label="Net Profit"     value={summary.profit}          tone={summary.profit >= 0 ? 'profit' : 'expense'} />
          <StatCard label="Transactions"   value={total} prefix="" tone="neutral" />
        </StatRow>
      )}

        {/* ── Transactions tab ── */}
        {activeTab === 'transactions' && (
          <>
            <AddTransactionPanel isAdmin={isAdmin} categories={categories} onCreated={refresh} />

            {/* Filters */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              <Input placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 260 }} />
              <Select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ width: 140 }}>
                <option value="">All types</option>
                <option value="income">Income only</option>
                <option value="expense">Expenses only</option>
              </Select>
              <Select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ width: 160 }}>
                <option value="">All categories</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
              <Input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} style={{ width: 150 }} />
              <Input type="date" value={filterTo}   onChange={e => setFilterTo(e.target.value)}   style={{ width: 150 }} />
              {(filterType || filterCat || filterFrom || filterTo) && (
                <Button onClick={() => { setFilterType(''); setFilterCat(''); setFilterFrom(''); setFilterTo(''); }} style={{ fontSize: 12 }}>
                  Clear filters
                </Button>
              )}
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg)' }}>
                    <th style={th}>Type</th>
                    <th style={th}>Date</th>
                    <th style={th}>Description</th>
                    <th style={th}>Category</th>
                    <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                    <th style={th}>Source</th>
                    <th style={th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>Loading…</td></tr>
                  )}
                  {!loading && transactions.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>No transactions found.</td></tr>
                  )}
                  {!loading && transactions.map(tx => (
                    <EditableRow
                      key={tx.id}
                      tx={tx}
                      categories={categories}
                      onSaved={refresh}
                      onDeleted={refresh}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end', marginTop: 12 }}>
                <Button onClick={() => loadTransactions(page - 1)} disabled={page <= 1} style={{ padding: '6px 10px', fontSize: 12 }}>← Prev</Button>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>Page {page} of {totalPages} ({total} total)</span>
                <Button onClick={() => loadTransactions(page + 1)} disabled={page >= totalPages} style={{ padding: '6px 10px', fontSize: 12 }}>Next →</Button>
              </div>
            )}
          </>
        )}

        {/* ── P&L Overview tab ── */}
        {activeTab === 'overview' && (
          <div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <div>
                <div style={labelStyle}>From</div>
                <Input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} style={{ width: 160 }} />
              </div>
              <div>
                <div style={labelStyle}>To</div>
                <Input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} style={{ width: 160 }} />
              </div>
            </div>
            {summary ? (
              <>
                <div style={{ marginBottom: 4, fontWeight: 600, color: 'var(--text)', fontSize: 13 }}>Monthly Income vs Expenses (last 12 months)</div>
                <MonthlyChart monthly={summary.monthly || []} />
                {summary.monthly?.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 12 }}>No data in this period.</div>}
              </>
            ) : <div style={{ color: 'var(--muted)' }}>Loading…</div>}
          </div>
        )}

        {/* ── Categories tab ── */}
        {activeTab === 'categories' && (
          <div style={{ maxWidth: 600 }}>
            <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--muted)' }}>Expense breakdown by category</div>
            <CategoryBreakdown categories={summary?.categories || []} />
          </div>
        )}

        {/* ── Cash Flow forecast tab ── */}
        {activeTab === 'forecast' && (
          <CashFlowForecast monthly={allTimeSummary?.monthly || []} />
        )}

        {/* ── Import tab ── */}
        {activeTab === 'import' && (
          <>
            <ImportPanel isAdmin={isAdmin} onImported={refresh} />
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
              Re-importing the same file is safe - duplicate rows are detected and skipped automatically.
            </div>
          </>
        )}
    </div>
  );
}